import axios from 'axios'
import type {
  ActivitiesFeedResponse,
  HistoryReconstructionStatus,
  FeedActivity,
  ActivitiesFeedMeta
} from '@/types/customer/ActivitiesFeed'

export default function useCustomerActivitiesFeed() {
  const fetchFeed = async (
    customerId: string,
    params?: { pageSize?: number; page?: number; type?: string[] }
  ): Promise<{
    response: ActivitiesFeedResponse
    reconstructionStatus: HistoryReconstructionStatus | null
  }> => {
    const response = await axios.get<ActivitiesFeedResponse>(`/customers/${customerId}/activities/feed`, { params })

    const meta = response.data?.meta as ActivitiesFeedMeta | undefined
    const reconstructionStatus: HistoryReconstructionStatus | null = meta?.reconstruction_status || null

    return {
      response: response.data,
      reconstructionStatus
    }
  }

  const wait = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  const fetchFeedWithRetries = async (
    customerId: string,
    params?: { pageSize?: number; page?: number; type?: string[] },
    onSuccess?: (result: {
      response: ActivitiesFeedResponse
      reconstructionStatus: HistoryReconstructionStatus | null
    }) => void
  ): Promise<{
    response: ActivitiesFeedResponse
    reconstructionStatus: HistoryReconstructionStatus | null
  }> => {
    const result = await fetchFeed(customerId, params)

    if (result.reconstructionStatus === 'succeeded') {
      return result
    }

    if (result.reconstructionStatus === 'failed') {
      return result
    }

    const retryDelays = [2500, 5000, 10000]

    Promise.resolve()
      .then(async () => {
        for (const delay of retryDelays) {
          await wait(delay)
          try {
            const retryResult = await fetchFeed(customerId, params)

            if (retryResult.reconstructionStatus === 'succeeded' && onSuccess) {
              onSuccess(retryResult)
              return
            }

            if (retryResult.reconstructionStatus === 'failed') {
              return
            }
          } catch (error) {
            console.error('Error during feed retry:', error)
            return
          }
        }
      })
      .catch((error) => {
        console.error('Error in feed retry promise chain:', error)
      })

    return result
  }

  const shouldUseFallback = (data: FeedActivity[], status: HistoryReconstructionStatus | null): boolean => {
    const isEmpty = data.length === 0
    const isPending = status === 'queued' || status === 'running'
    return isEmpty && isPending
  }

  return {
    fetchFeed,
    fetchFeedWithRetries,
    shouldUseFallback
  }
}
